'use client';

import React, { forwardRef, useRef } from 'react';
import {
  arrow,
  autoUpdate,
  flip,
  limitShift,
  offset,
  safePolygon,
  shift,
  size,
  useClick,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import PortalWrp, { PortalId } from '@creditflowresearch/uilib/Layout/PortalWrp';
import { getArrowStyles } from './getArrowStyles';
import { useFloatingControls } from './useFloatingControls';
import type {
  IFloatingControls,
  IFloatingElementProps,
  IFloatingRef,
  IInteractions,
  IPositioning,
  IReferenceElementProps,
} from './interfaces';

export interface IProps {
  floating: (args: IFloatingElementProps) => React.ReactNode,
  reference: (args: IReferenceElementProps) => React.ReactNode,
  controls: IFloatingControls,
  interactions: IInteractions,
  positioning: IPositioning,
  renderOnlyVisible?: boolean,
}

const Floating = forwardRef((
  {
    controls,
    floating,
    reference,
    interactions,
    positioning: {
      placement = 'bottom',
      isFlipEnabled = true,
      preventOverflowEnabled = true,
      positionFixed = true,
      usePortal = true,
      minPadding = 17,
      offsetOptions = 0,
      shiftPadding = minPadding,
      flipPadding = minPadding,
      boundary,
    },
    renderOnlyVisible = false,
  }: IProps,
  ref: React.ForwardedRef<IFloatingRef>,
) => {
  const arrowRef = useRef<HTMLDivElement>(null);
  const arrowAnchorRef = useRef<HTMLElement>(null);

  const { context } = useFloating({
    ...useFloatingControls(ref, controls),
    placement,
    strategy: positionFixed ? 'fixed' : 'absolute',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(offsetOptions),
      preventOverflowEnabled && shift({
        padding: shiftPadding,
        limiter: limitShift(),
        boundary: boundary ?? 'clippingAncestors',
      }),
      isFlipEnabled && flip({
        padding: flipPadding,
        boundary: boundary ?? 'clippingAncestors',
      }),
      /**
       * Exposes the room left on the chosen side, so content taller than the viewport can cap its height
       * and scroll instead of running off screen.
       */
      size({
        padding: flipPadding,
        boundary: boundary ?? 'clippingAncestors',
        apply: ({ availableHeight, elements }) => {
          elements.floating.style.setProperty('--floating-available-height', `${ Math.max(0, availableHeight) }px`);
        },
      }),
      arrow({ element: arrowRef }),
    ],
  });

  const isEnabled = (interaction?: { enabled: boolean }): boolean => !controls.disabled && !!interaction?.enabled;
  const isClickEnabled = isEnabled(interactions.click);

  const click = useClick(context, { enabled: isClickEnabled });
  const hover = useHover(context, {
    enabled: isEnabled(interactions.hover),
    handleClose: interactions.hover?.handleClose ? safePolygon() : null,
  });
  const focus = useFocus(context, { enabled: isEnabled(interactions.focus) });
  const role = useRole(context, { enabled: !!interactions.role, role: interactions.role });
  const dismiss = useDismiss(context, {
    outsidePress: interactions.dismiss.onOutsidePress,
    ancestorScroll: interactions.dismiss.onAncestorScroll,
  });
  const { getReferenceProps, getFloatingProps } = useInteractions([click, hover, focus, role, dismiss]);

  return (
    <>
      { reference({
        context,
        arrowAnchorRef,
        referenceProps: {
          ref: context.refs.setReference,
          ...getReferenceProps({
            onClick: (event: React.MouseEvent) => {
              if (isClickEnabled && interactions.click.preventDefault) {
                event.preventDefault();
              }
            },
          }),
        },
      }) }
      <PortalWrp usePortal={ usePortal } portalId={ PortalId.PORTAL_POPPER_ID }>
        { (!renderOnlyVisible || context.open) && floating({
          open: context.open,
          placement: context.placement,
          contentProps: {
            ref: context.refs.setFloating,
            style: context.floatingStyles,
            ...getFloatingProps(),
          },
          arrowProps: {
            ref: arrowRef,
            style: getArrowStyles(context, arrowRef, arrowAnchorRef),
          },
        }) }
      </PortalWrp>
    </>
  );
});

export default Floating;
